import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Send, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = ({ user }) => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  useEffect(() => {
    if (matchId && matches.length > 0) {
      const match = matches.find(m => m.match.id === matchId);
      if (match) {
        selectMatch(match);
      }
    }
  }, [matchId, matches]);

  const fetchMatches = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/matches/accepted`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const selectMatch = async (match) => {
    setSelectedMatch(match);
    await fetchMessages(match.match.id);
  };

  const fetchMessages = async (matchId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/messages/${matchId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedMatch) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/messages`,
        {
          match_id: selectedMatch.match.id,
          receiver_id: selectedMatch.user.id,
          content: newMessage,
          message_type: 'text'
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex flex-col">
      <div className="max-w-7xl mx-auto w-full px-6 py-4">
        <Button
          data-testid="back-to-matches-btn"
          variant="ghost"
          onClick={() => navigate('/matches')}
          className="text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Matches
        </Button>
      </div>

      <div className="flex-1 max-w-7xl mx-auto w-full px-6 pb-6 flex gap-6 overflow-hidden">
        {/* Matches List */}
        <Card className="w-80 flex-shrink-0 shadow-lg">
          <CardHeader>
            <CardTitle>Conversations</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {matches.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No matches yet
              </div>
            ) : (
              <div className="space-y-1">
                {matches.map(({ match, user: matchUser }) => (
                  <button
                    key={match.id}
                    data-testid={`conversation-${match.id}`}
                    onClick={() => selectMatch({ match, user: matchUser })}
                    className={`w-full p-4 flex items-center gap-3 hover:bg-rose-50 transition-colors ${
                      selectedMatch?.match.id === match.id ? 'bg-rose-100' : ''
                    }`}
                  >
                    {matchUser.picture ? (
                      <img src={matchUser.picture} alt={matchUser.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-rose-200 flex items-center justify-center">
                        <User className="w-6 h-6 text-rose-600" />
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-rose-900">{matchUser.name}</div>
                      <div className="text-sm text-gray-500 truncate">View messages</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages */}
        <Card className="flex-1 shadow-lg flex flex-col">
          {selectedMatch ? (
            <>
              <CardHeader className="border-b">
                <div className="flex items-center gap-3">
                  {selectedMatch.user.picture ? (
                    <img src={selectedMatch.user.picture} alt={selectedMatch.user.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-rose-200 flex items-center justify-center">
                      <User className="w-6 h-6 text-rose-600" />
                    </div>
                  )}
                  <CardTitle>{selectedMatch.user.name}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="messages-container">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  messages.map((message) => (
                    message.message_type === 'system' ? (
                      <div key={message.id} data-testid={`message-${message.id}`} className="flex justify-center">
                        <div className="max-w-md bg-amber-50 border border-amber-200 text-gray-700 text-sm rounded-2xl px-4 py-3 whitespace-pre-line" data-testid="system-message">
                          {message.content}
                        </div>
                      </div>
                    ) : (
                    <div
                      key={message.id}
                      data-testid={`message-${message.id}`}
                      className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                          message.sender_id === user.id
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p>{message.content}</p>
                        <p className="text-xs mt-1 opacity-70">
                          {new Date(message.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    )
                  ))
                )}
              </CardContent>
              <div className="border-t p-4">
                <form onSubmit={sendMessage} className="flex gap-2">
                  <Input
                    data-testid="message-input"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                  />
                  <Button
                    data-testid="send-message-btn"
                    type="submit"
                    className="bg-rose-500 hover:bg-rose-600"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center text-gray-500">
              Select a conversation to start messaging
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;